/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React, { useEffect, useRef } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { CheckboxField } from '../../../../../Components/FormFields/CheckboxField';

/**
 * ContractApproval Component
 * @returns {JSX.Element}
 * @constructor
 */
export function ContractApproval() {
  const { setFieldValue } = useFormikContext();
  const addedApprovalFormFieldToFormik = useRef(false);
  const { t } = useTranslation('translations');
  const {
    values: { rateBundle },
  } = useFormikContext();

  // To ensure the field.onChange(e) does work as expected, we need to add the value to the formik context
  useEffect(() => {
    if (!addedApprovalFormFieldToFormik.current) {
      addedApprovalFormFieldToFormik.current = true;
      rateBundle.contractTextBlocks.forEach((contractTextBlock) => {
        if (contractTextBlock.isConfirmationRequired) {
          setFieldValue(`approvalTextBlock-${contractTextBlock.id}`, false);
        }
      });
    }
  }, [rateBundle, setFieldValue]);

  /**
   * Render document download link
   * @param attachedDocument
   * @returns {React.JSX.Element}
   */
  const renderDocumentDownload = (attachedDocument) => (
    <Box
      mt={2}
      mb={2}
      sx={{
        display: 'flex',
        justifyContent: 'left',
      }}
    >
      <FileDownloadIcon />
      {' '}
      <a
        href={attachedDocument.url}
        style={{
          color: '#6bb52c',
          paddingLeft: '8px',
        }}
        download
        rel="noreferrer noopener"
        target="_blank"
      >
        {attachedDocument.fileName}
      </a>
    </Box>
  );

  /**
   * Render link to external document
   * @param attachedExternalUrlDto
   * @returns {JSX.Element}
   */
  const renderExternalUrlLink = (attachedExternalUrlDto) => (
    <Box
      mt={2}
      mb={2}
      sx={{
        display: 'flex',
        justifyContent: 'left',
      }}
    >
      <OpenInNewIcon />
      {' '}
      <a
        href={attachedExternalUrlDto.link}
        style={{
          color: '#6bb52c',
          paddingLeft: '8px',
        }}
        rel="noreferrer noopener"
        target="_blank"
      >
        {attachedExternalUrlDto.title}
      </a>
    </Box>
  );

  /**
   * Render user approval form fields
   * @param contractTextBlock
   * @returns {React.JSX.Element}
   */
  const renderApprovalFormField = (contractTextBlock) => (
    <Box mt={2} key={contractTextBlock.id}>
      <CheckboxField
        name={`approvalTextBlock-${contractTextBlock.id}`}
        label={`${contractTextBlock.title}: ${contractTextBlock.text}`}
        required={contractTextBlock.isConfirmationRequired}
      />
      {contractTextBlock.attachedDocument ? renderDocumentDownload(contractTextBlock.attachedDocument) : null}
      {contractTextBlock.attachedExternalUrlDto ? renderExternalUrlLink(contractTextBlock.attachedExternalUrlDto) : null}
      <hr />
    </Box>
  );

  /**
   * Render a description text block which doesn't need approval
   * @param contractTextBlock
   * @returns {React.JSX.Element}
   */
  const renderTextBlockDescription = (contractTextBlock) => (
    <Box mt={2} key={contractTextBlock.id}>
      <Box key={`textBlock${contractTextBlock.id}`}>
        {contractTextBlock.title}
        :
        {' '}
        {contractTextBlock.text}
      </Box>
      {contractTextBlock.attachedDocument ? renderDocumentDownload(contractTextBlock.attachedDocument) : null}
      {contractTextBlock.attachedExternalUrlDto ? renderExternalUrlLink(contractTextBlock.attachedExternalUrlDto) : null}
      <hr />
    </Box>

  );

  /**
   * Render contract text block
   * Case 1: text block needs user approval
   * Case 2: text block only displays text and needs no approval
   * @returns {React.JSX.Element}
   */
  const renderContractTextBlocks = () => rateBundle.contractTextBlocks.map((contractTextBlock) => {
    if (contractTextBlock.isConfirmationRequired) {
      return renderApprovalFormField(contractTextBlock);
    }
    return renderTextBlockDescription(contractTextBlock);
  });

  return (
    <Grid container mt={4}>
      <Grid item xs={12}>
        <Typography
          sx={{
            fontWeight: 'bold',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            color: '#49ad33',
          }}
        >
          {t('contractApprovalHeader')}
        </Typography>
        {renderContractTextBlocks()}
      </Grid>
    </Grid>

  );
}
